import type React from 'react';
import { cva } from 'class-variance-authority';
import KoobTooltip from './KoobTooltip';
import cn from '../utils/cn';

const labelVariants = cva('block font-medium transition-colors duration-200', {
  variants: {
    variant: {
      default: 'text-gray-700',
      outline: 'text-gray-600',
      ghost: 'text-gray-500',
    },
    size: {
      sm: 'text-sm mb-1',
      default: 'text-sm mb-2',
      lg: 'text-base mb-2',
    },
    disabled: {
      true: 'text-gray-400 cursor-not-allowed',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
});

const requiredIndicatorVariants = cva('ml-1', {
  variants: {
    variant: {
      default: 'text-red-500',
      outline: 'text-red-400',
      ghost: 'text-red-400',
    },
    size: {
      sm: 'text-sm',
      default: 'text-sm',
      lg: 'text-base',
    },
    disabled: {
      true: 'text-gray-400 cursor-not-allowed',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
});

type KoobInputLabelProps = {
  label?: React.ReactNode;
  required?: boolean;
  htmlFor?: string;
  className?: string;
  children?: React.ReactNode;
  helpText?: React.ReactNode;
  tooltipDescription?: React.ReactNode;
  tooltipColor?: string;
  tooltipPosition?: string;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'sm' | 'default' | 'lg';
  disabled?: boolean;
};

export default function KoobInputLabel({
  label,
  required = false,
  htmlFor,
  className,
  children,
  helpText,
  tooltipDescription,
  tooltipColor = 'purple',
  tooltipPosition = 'left-top',
  variant = 'default',
  size = 'default',
  disabled = false,
}: KoobInputLabelProps) {
  return (
    <div className={cn('w-full', className)}>
      <label
        htmlFor={htmlFor}
        className={cn(
          labelVariants({ variant, size, disabled }),
          'flex items-center gap-1.5',
        )}
      >
        <span>{label}</span>
        {required && (
          <span
            className={requiredIndicatorVariants({ variant, size, disabled })}
            aria-label="required"
          >
            *
          </span>
        )}
        {tooltipDescription && (
          <KoobTooltip
            tip={tooltipDescription}
            position={tooltipPosition}
            hasArrow={false}
            color={tooltipColor}
          >
            <i className="fa-regular fa-circle-info" />
          </KoobTooltip>
        )}
      </label>
      {children}
      {helpText && (
        <p
          className={cn(
            'mt-1 text-xs',
            disabled ? 'text-gray-400' : 'text-gray-500',
          )}
        >
          {helpText}
        </p>
      )}
    </div>
  );
}
